import React, { useRef, useState } from 'react';
import { LinkButton } from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu';
import AppIcon from '@atlaskit/icon/core/app';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { ButtonItem, LinkItem, MenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { lazy, LazySuspense } from '@atlassian/react-loosely-lazy';
import {
	withConnectHost,
	isConnectDialogWebItem,
} from '@atlassian/jira-connect-utils/src/common/utils/ecosystem-connect-host.tsx';
import type { BoardToolWebItem, BoardToolSectionSubItem } from '../../../../types.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const AsyncBoardToolWebPanelView = lazy(() =>
	import(/* webpackChunkName: "async-board-tool-web-panel" */ './board-tool-web-panel-view').then(
		({ BoardToolWebPanelView }) => ({ default: BoardToolWebPanelView }),
	),
);

interface Props {
	subItem: BoardToolSectionSubItem;
	onClickWebItem?: () => void;
	isTabNavigation?: boolean;
}

export function BoardToolWebItemView({
	subItem,
	mode = 'dropdown-item',
	onClickWebItem,
	isTabNavigation = false,
}: {
	subItem: BoardToolWebItem;
	mode?: 'button' | 'dropdown-item';
	onClickWebItem?: () => void;
	isTabNavigation?: boolean;
}) {
	const { url, cssClass, label } = subItem;
	const webItemRef = useRef<HTMLAnchorElement>(null);

	const onWebItemClick = (e: React.MouseEvent | React.KeyboardEvent) => {
		/**
		 * When using the Board & Backlog header menu (the rightmost meatball menu),
		 * the button gets unmounted because the menu closes upon clicking the button.
		 * As a result, webItemRef.current becomes null when connectHost.triggerWebItem is called.
		 * Therefore, we need to store the webItemRef in a variable for proper reference.
		 */
		const currentNode = webItemRef.current;
		withConnectHost((connectHost) => {
			if (currentNode) {
				connectHost.triggerWebItem(currentNode, cssClass ?? '', url);
			}
		});
		if (isConnectDialogWebItem(cssClass ?? '')) {
			e.preventDefault();
			e.stopPropagation();
			if (onClickWebItem) onClickWebItem();
		}
	};

	return (
		<>
			{mode === 'dropdown-item' ? (
				<Tooltip content={isTabNavigation ? label : undefined}>
					<LinkItem
						onClick={onWebItemClick}
						iconBefore={getWillShowNav4() && <AppIcon label={label} color={token('color.icon')} />}
						testId="software-connect-toolbar.common.ui.tool-sections.board-tool-section-view.board-tool-sub-item.link-item"
						ref={webItemRef}
						href={url}
					>
						{label}
					</LinkItem>
				</Tooltip>
			) : (
				<LinkButton
					testId="software-connect-toolbar.common.ui.tool-sections.board-tool-section-view.board-tool-sub-item.link-item"
					onClick={onWebItemClick}
					ref={webItemRef}
					href={url}
				>
					{label}
				</LinkButton>
			)}
		</>
	);
}

export function BoardToolSubItem({ subItem, onClickWebItem, isTabNavigation = false }: Props) {
	const [isOpen, setIsOpen] = useState(false);
	if (subItem.type === 'webItem') {
		return (
			<BoardToolWebItemView
				onClickWebItem={onClickWebItem}
				subItem={subItem}
				isTabNavigation={isTabNavigation}
			/>
		);
	}
	if (subItem.type === 'section') {
		const content = subItem.subItems?.map((item) => (
			<BoardToolSubItem key={item.id} subItem={item} isTabNavigation={isTabNavigation} />
		));

		if (subItem.label === '') {
			return <>{content}</>;
		}

		if (isTabNavigation) {
			return (
				<Popup
					isOpen={isOpen}
					onClose={() => setIsOpen(false)}
					placement="left-start"
					trigger={(triggerProps) => (
						<Tooltip content={subItem.label}>
							<ButtonItem
								{...triggerProps}
								iconBefore={
									getWillShowNav4() && <AppIcon label={subItem.label} color={token('color.icon')} />
								}
								iconAfter={<ChevronRightIcon label="" color={token('color.icon')} />}
								onClick={() => setIsOpen(!isOpen)}
								testId="software-connect-toolbar.common.ui.tool-sections.board-tool-section-view.board-tool-sub-item.popup-menu-trigger"
							>
								{subItem.label}
							</ButtonItem>
						</Tooltip>
					)}
					content={() => (
						<MenuGroup maxWidth={240} onClick={(e) => e.stopPropagation()}>
							<Section>{content}</Section>
						</MenuGroup>
					)}
				/>
			);
		}

		return (
			<DropdownMenu
				placement="right-end"
				trigger={subItem.label}
				testId="software-connect-toolbar.common.ui.tool-sections.board-tool-section-view.board-tool-sub-item.dropdown-menu"
			>
				{content}
			</DropdownMenu>
		);
	}
	if (subItem.type === 'webPanel') {
		return (
			<JSErrorBoundary
				id="board-tool-web-panel-view"
				packageName="@atlassian/jira-software-connect-toolbar"
				teamName="a4t-tanuki"
			>
				<LazySuspense fallback="Loading...">
					<AsyncBoardToolWebPanelView webPanel={subItem} />
				</LazySuspense>
			</JSErrorBoundary>
		);
	}

	return null;
}
